import { z } from "zod"
import { MemberRoleSchema, PaymentMethodSchema } from "../constants/index.js"

// ── Household ────────────────────────────────────────────────────────────────

export const CreateHouseholdSchema = z.object({
    name: z.string().min(1).max(100),
})

export const InviteMemberSchema = z.object({
    email: z.string().email(),
})

export const AcceptInviteSchema = z.object({
    token: z.string().min(1),
})

export const UpdateMemberSchema = z.object({
    memberId: z.string().uuid(),
    displayName: z.string().min(1).max(50),
})

export const RemoveMemberSchema = z.object({
    memberId: z.string().uuid(),
})

export const AddMemberSchema = z.object({
    displayName: z.string().min(1).max(50),
})

export const UpdateHouseholdSettingsSchema = z.object({
    incomeShiftEnabled: z.boolean().optional(),
    incomeShiftDay: z.number().int().min(1).max(28).optional(),
})

export const UpdateProfileSchema = z.object({
    name: z.string().min(1).max(100),
})

// ── Category ─────────────────────────────────────────────────────────────────

export const CreateCategorySchema = z.object({
    groupId: z.string().uuid(),
    name: z.string().min(1).max(100),
    paymentMethod: PaymentMethodSchema,
    paymentSource: z.string().max(100).optional(),
    isRecurring: z.boolean(),
    isVariable: z.boolean(),
    memberId: z.string().uuid().optional(),
})

/** Lightweight schema for creating a category inline (e.g. during import review) */
export const QuickCreateCategorySchema = z.object({
    groupId: z.string().uuid(),
    name: z.string().min(1).max(100),
})

export const UpdateCategorySchema = z.object({
    id: z.string().uuid(),
    name: z.string().min(1).max(100).optional(),
    paymentMethod: PaymentMethodSchema.optional(),
    paymentSource: z.string().max(100).nullable().optional(),
    isRecurring: z.boolean().optional(),
    isVariable: z.boolean().optional(),
    memberId: z.string().uuid().nullable().optional(),
})

export const ReorderCategoriesSchema = z.object({
    categoryIds: z.array(z.string().uuid()).min(1),
})

export const ListCategoryTransactionsSchema = z.object({
    categoryId: z.string().uuid(),
    limit: z.number().int().min(1).max(200).default(50),
    offset: z.number().int().min(0).default(0),
})

// ── Income ───────────────────────────────────────────────────────────────────

export const CreateIncomeSourceSchema = z.object({
    memberId: z.string().uuid().nullable().default(null),
    name: z.string().min(1).max(100),
    expectedAmount: z.number().int().min(0),
    isVariable: z.boolean(),
    categoryRuleIds: z.array(z.string().uuid()).min(1),
})

export const UpdateIncomeSourceSchema = z.object({
    id: z.string().uuid(),
    name: z.string().min(1).max(100).optional(),
    expectedAmount: z.number().int().min(0).optional(),
    isVariable: z.boolean().optional(),
    memberId: z.string().uuid().nullable().optional(),
    categoryRuleIds: z.array(z.string().uuid()).min(1).optional(),
})

export const DeleteIncomeSourceSchema = z.object({
    id: z.string().uuid(),
})

export const IncomeHistorySchema = z.object({
    sourceId: z.string().uuid(),
    months: z.number().int().min(1).max(24).default(12),
})

export const CreateIncomeFromTransactionSchema = z.object({
    transactionId: z.string().uuid(),
    name: z.string().trim().min(1).max(100),
    memberId: z.string().uuid().nullable().default(null),
})

// ── Budget ───────────────────────────────────────────────────────────────────

export const CreateBudgetSchema = z.object({
    name: z.string().min(1).max(100),
    budgetedAmount: z.number().int().min(0),
    categoryIds: z.array(z.string().uuid()),
})

export const UpdateBudgetSchema = z.object({
    id: z.string().uuid(),
    name: z.string().min(1).max(100).optional(),
    budgetedAmount: z.number().int().min(0).optional(),
    categoryIds: z.array(z.string().uuid()).optional(),
    isActive: z.boolean().optional(),
})

export const DeleteBudgetSchema = z.object({
    id: z.string().uuid(),
})

export const ReorderBudgetsSchema = z.object({
    budgetIds: z.array(z.string().uuid()).min(1),
})

export const GetOverviewSchema = z.object({
    year: z.number().int().min(2020).max(2100),
    month: z.number().int().min(1).max(12),
})

export const GetBudgetTransactionsSchema = z.object({
    budgetId: z.string().uuid(),
    year: z.number().int().min(2020).max(2100),
    month: z.number().int().min(1).max(12),
})

export const UpdateBillEntrySchema = z.object({
    recurringBillId: z.string().uuid(),
    year: z.number().int().min(2020).max(2100),
    month: z.number().int().min(1).max(12),
    expectedAmount: z.number().int().min(0),
})

export const UpdateLoanEntrySchema = z.object({
    loanId: z.string().uuid(),
    year: z.number().int().min(2020).max(2100),
    month: z.number().int().min(1).max(12),
    expectedAmount: z.number().int().min(0),
})

// ── Transaction ──────────────────────────────────────────────────────────────

export const ListTransactionsSchema = z.object({
    year: z.number().int().min(2020).max(2100),
    month: z.number().int().min(1).max(12),
    categoryId: z.string().uuid().optional(),
    memberId: z.string().uuid().optional(),
    uncategorizedOnly: z.boolean().optional(),
    groupByAccount: z.boolean().optional().default(false),
    categoryFilter: z.string().uuid().optional(),
    limit: z.number().int().min(1).max(500).default(100),
    offset: z.number().int().min(0).default(0),
})

export const SearchTransactionsSchema = z.object({
    query: z.string().max(200).optional(),
    categoryId: z.string().uuid().optional(),
    accountId: z.string().uuid().optional(),
    isTransfer: z.boolean().optional(),
    isExcluded: z.boolean().optional(),
    /** Omit for all rows; hidden rows are kept out of lists, not out of sums */
    isHidden: z.boolean().optional(),
    needsReview: z.boolean().optional(),
    dateFrom: z.string().optional(),
    dateTo: z.string().optional(),
    sortBy: z.enum(["date", "amount", "description"]).default("date"),
    sortDir: z.enum(["asc", "desc"]).default("desc"),
    limit: z.number().int().min(1).max(200).default(50),
    offset: z.number().int().min(0).default(0),
})

export const SetExcludedSchema = z.object({
    transactionId: z.string().uuid(),
    isExcluded: z.boolean(),
})

/** Hide a booked transaction from lists. It still counts in every number. */
export const SetHiddenSchema = z.object({
    transactionId: z.string().uuid(),
    isHidden: z.boolean(),
})

/**
 * Hide a card reservation from lists. Reservations have no stored id, so
 * they're named by what the bank shows for them.
 */
export const SetPendingHiddenSchema = z.object({
    accountId: z.string().uuid(),
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    amount: z.number().int(),
    description: z.string().max(500),
    isHidden: z.boolean(),
})

export const ImportTransactionsSchema = z.object({
    fileContent: z.string().min(1),
    fileName: z.string().min(1),
    memberId: z.string().uuid().nullable(),
})

export const DiscardImportSchema = z.object({
    batchId: z.string().uuid(),
})

export const ConfirmImportSchema = z.object({
    batchId: z.string().uuid(),
    accounts: z.array(
        z.object({
            accountNumber: z.string(),
            friendlyName: z.string().max(100).nullable(),
            memberId: z.string().uuid().nullable(),
            approve: z.boolean(),
        }),
    ),
    transactions: z.array(
        z.object({
            id: z.string().uuid(),
            categoryId: z.string().uuid().nullable(),
            skip: z.boolean(),
            createRule: z.boolean(),
            isTransfer: z.boolean().default(false),
            createTransferRule: z.boolean().default(false),
            rulePattern: z
                .string()
                .trim()
                .min(3)
                .max(200)
                .nullable()
                .optional(),
        }),
    ),
})

export const CategorizeTransactionSchema = z.object({
    transactionId: z.string().uuid(),
    categoryId: z.string().uuid(),
    createRule: z.boolean(),
})

export const CreateManualTransactionSchema = z.object({
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    description: z.string().min(1).max(500),
    amount: z.number().int(),
    categoryId: z.string().uuid().optional(),
    memberId: z.string().uuid(),
})

// ── Savings ──────────────────────────────────────────────────────────────────

export const CreateSavingsGoalSchema = z.object({
    name: z.string().min(1).max(100),
    monthlyTarget: z.number().int().min(0),
    memberId: z.string().uuid().optional(),
})

export const UpdateSavingsGoalSchema = z.object({
    id: z.string().uuid(),
    name: z.string().min(1).max(100).optional(),
    monthlyTarget: z.number().int().min(0).optional(),
    memberId: z.string().uuid().nullable().optional(),
})

// ── Category rules ────────────────────────────────────────────────────────────

export const CreateCategoryRuleSchema = z.object({
    categoryId: z.string().uuid(),
    pattern: z.string().min(1).max(200),
    matchType: z.enum(["contains", "exact", "starts_with"]).default("contains"),
    confidence: z.number().int().min(1).max(100).default(100),
})

export const DeleteCategoryRuleSchema = z.object({
    id: z.string().uuid(),
})

export const RemoveDuplicateCategoryRulesSchema = z.object({
    primaryRuleId: z.string().uuid(),
    redundantRuleIds: z.array(z.string().uuid()).min(1),
})

export const UpdateCategoryRuleSchema = z.object({
    id: z.string().uuid(),
    categoryId: z.string().uuid().optional(),
    pattern: z.string().min(1).max(200).optional(),
    matchType: z.enum(["contains", "exact", "starts_with"]).optional(),
    confidence: z.number().int().min(1).max(100).optional(),
})

export const PreviewRuleImpactSchema = z.object({
    pattern: z.string().min(1).max(200),
    matchType: z.enum(["contains", "exact", "starts_with"]).default("contains"),
    categoryId: z.string().uuid(),
    existingRuleId: z.string().uuid().optional(),
})

export const ApplyRuleRecategorizationSchema = z.object({
    pattern: z.string().min(1).max(200),
    matchType: z.enum(["contains", "exact", "starts_with"]).default("contains"),
    categoryId: z.string().uuid(),
    includeManualOverrides: z.boolean().default(false),
    saveRule: z.boolean().default(true),
    existingRuleId: z.string().uuid().optional(),
})

// ── Member role ───────────────────────────────────────────────────────────────
export { MemberRoleSchema }

// ── Bank account ─────────────────────────────────────────────────────────────

export const CreateBankAccountSchema = z.object({
    accountNumber: z.string().min(1).max(20),
    friendlyName: z.string().max(100).optional(),
    memberId: z.string().uuid().optional(),
    bankSource: z.string().default("nordea"),
})

export const UpdateBankAccountSchema = z.object({
    id: z.string().uuid(),
    friendlyName: z.string().max(100).nullable().optional(),
    memberId: z.string().uuid().nullable().optional(),
    iban: z.string().max(34).nullable().optional(),
})

export const DeleteBankAccountSchema = z.object({
    id: z.string().uuid(),
})

// ── Transfer rules ────────────────────────────────────────────────────────────

export const CreateTransferRuleSchema = z.object({
    pattern: z.string().min(1).max(200),
    matchType: z.enum(["contains", "exact", "starts_with"]).default("contains"),
    label: z.string().max(100).nullable().optional(),
})

export const DeleteTransferRuleSchema = z.object({
    id: z.string().uuid(),
})

// ── Insights ─────────────────────────────────────────────────────────────────

export const SpendingBreakdownSchema = z.object({
    year: z.number().int().min(2020).max(2100),
    month: z.number().int().min(1).max(12),
})

export const FlowDataSchema = z.object({
    year: z.number().int().min(2020).max(2100),
    month: z.number().int().min(1).max(12),
})

export const InsightsOverviewSchema = z.object({
    year: z.number().int().min(2020).max(2100),
    month: z.number().int().min(1).max(12),
    categoryFilter: z.string().optional(),
})

export const CategoryTransactionsSchema = z.object({
    year: z.number().int().min(2020).max(2100),
    month: z.number().int().min(1).max(12),
    categoryId: z.string().uuid().nullable(),
})

export const TrendsSchema = z.object({
    months: z.number().int().min(1).max(12).default(6),
})

export const MonthForecastSchema = z.object({
    year: z.number().int().min(2020).max(2100),
    month: z.number().int().min(1).max(12),
})

export const BatchCategorizeSchema = z.object({
    transactionIds: z.array(z.string().uuid()).min(1).max(100),
    categoryId: z.string().uuid(),
})

export const SetRecurringSchema = z.object({
    transactionId: z.string().uuid(),
    isRecurring: z.boolean().nullable(),
})

export const SetTransferFlagSchema = z.object({
    transactionId: z.string().uuid(),
    isTransfer: z.boolean(),
})

export const RecurringBillStatusSchema = z.enum([
    "unpaid",
    "paid",
    "partial",
    "credited",
])

export const PredictionConfidenceSchema = z.enum(["low", "medium", "high"])

export const PredictionTrendSchema = z.enum([
    "stable",
    "increasing",
    "decreasing",
])

export const ListRecurringBillsSchema = z.object({})

export const CreateRecurringBillSchema = z.object({
    name: z.string().trim().min(1).max(100),
    categoryRuleIds: z.array(z.string().uuid()).min(1),
})

export const UpdateRecurringBillSchema = z.object({
    id: z.string().uuid(),
    name: z.string().trim().min(1).max(100).optional(),
    isActive: z.boolean().optional(),
    categoryRuleIds: z.array(z.string().uuid()).min(1).optional(),
})

export const CreateRecurringBillFromTransactionSchema = z.object({
    transactionId: z.string().uuid(),
    name: z.string().trim().min(1).max(100),
})

export const DeleteRecurringBillSchema = z.object({
    id: z.string().uuid(),
})

export const RecurringBillConflictsSchema = z.object({
    billId: z.string().uuid(),
})

export const RecurringBillHistorySchema = z.object({
    billId: z.string().uuid(),
    months: z.number().int().min(1).max(24).default(12),
})

/** Every bill's per-period cost over the last `months` budget periods */
export const RecurringBillHistoryAllSchema = z.object({
    months: z.number().int().min(3).max(36).default(12),
})

// ── Savings ──────────────────────────────────────────────────────────────────

/** Net saved per pot (category in the "Sparande" group) over the last `months` budget periods */
export const SavingsHistorySchema = z.object({
    months: z.number().int().min(3).max(36).default(12),
})

/** A pot's most recent countable transactions */
export const SavingsTransactionsSchema = z.object({
    categoryId: z.string().uuid(),
    limit: z.number().int().min(1).max(100).default(20),
})

// ── Import progress ──────────────────────────────────────────────────────────

const TxnOverrideSchema = z.object({
    categoryId: z.string().uuid().nullable(),
    skip: z.boolean(),
    createRule: z.boolean(),
    isTransfer: z.boolean(),
    createTransferRule: z.boolean(),
    rulePattern: z.string().min(3).max(200).nullable(),
    isManualOverride: z.boolean(),
    autoAppliedRulePattern: z.string().nullable(),
})

const AccountOverrideSchema = z.object({
    approve: z.boolean(),
    friendlyName: z.string(),
    memberId: z.string(),
})

export const SaveImportProgressSchema = z.object({
    batchId: z.string().uuid(),
    reviewState: z.object({
        version: z.number().int().positive(),
        transactionOverrides: z.record(z.string().uuid(), TxnOverrideSchema),
        accountOverrides: z.record(z.string().min(1), AccountOverrideSchema),
    }),
})

// ── Loans ────────────────────────────────────────────────────────────────────

export const ListLoansSchema = z.object({})

export const CreateLoanSchema = z.object({
    name: z.string().trim().min(1).max(100),
    totalAmount: z.number().int().min(0).nullable().optional(),
    interestRate: z.number().int().min(0).nullable().optional(),
    nextPaymentOverride: z.number().int().min(0).nullable().optional(),
    categoryRuleIds: z.array(z.string().uuid()).min(1),
})

export const UpdateLoanSchema = z.object({
    id: z.string().uuid(),
    name: z.string().trim().min(1).max(100).optional(),
    totalAmount: z.number().int().min(0).nullable().optional(),
    interestRate: z.number().int().min(0).nullable().optional(),
    nextPaymentOverride: z.number().int().min(0).nullable().optional(),
    isActive: z.boolean().optional(),
    categoryRuleIds: z.array(z.string().uuid()).min(1).optional(),
})

export const DeleteLoanSchema = z.object({
    id: z.string().uuid(),
})

export const LoanHistorySchema = z.object({
    loanId: z.string().uuid(),
    months: z.number().int().min(1).max(24).default(12),
})

// ── Sync ──────────────────────────────────────────────────────────────────────

export const SyncLogsSchema = z.object({
    limit: z.number().int().min(1).max(100).default(20),
})

export const SyncLogDetailSchema = z.object({
    syncLogId: z.string().uuid(),
})

export const FlaggedTransactionsSchema = z.object({
    limit: z.number().int().min(1).max(200).default(50),
})

export const ResolveFlaggedSchema = z.object({
    transactionId: z.string().uuid(),
    categoryId: z.string().uuid().nullable(),
    /** If true, also create/update a category rule for future auto-assignment */
    createRule: z.boolean().default(false),
    rulePattern: z.string().optional(),
})

export const LinkBankAccountEbSchema = z.object({
    bankAccountId: z.string().uuid(),
    ebAccountUid: z.string().min(1),
})
